import { FC, ReactNode, useMemo, useRef } from "react";
import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSRowItem,
  MDSRowItemAppearance,
  MDSRowItemSlotName,
  MDSSpacer,
  MDSTextColor,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { MDSPanelWidths } from "@/mds/foundations";
import { ItemListRowDropdown } from "@/components/item-list/rows/components";
import { ItemListItemRowData } from "@/components/item-list/types";
import { ReactMouseEvent } from "@/domains/react/types";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { formatDateString } from "@/domains/date/date";
import { formatCalendarEventTime } from "@/domains/calendar/formating";

export interface ItemListAsyncCalendarEventRowViewProps {
  itemRowData: ItemListItemRowData;
  startTime: string | null | undefined;
  endTime: string | null | undefined;
  isAllDay: boolean;
  isHighlighted: boolean;
  isHoverable: boolean;
  isDisabled?: boolean;
  hasAssociatedNote: boolean;
  handleItemClick: (event: ReactMouseEvent, canClickItem?: boolean) => void;
  handleMouseEnter: () => void;
}

const UPCOMING_EVENT_THRESHOLD_MINUTES = 60;
const IMMINENT_EVENT_THRESHOLD_MINUTES = 10;

export const getImminentEventLabels = ({ startTime }: { startTime: DateTime }) => {
  const diffMinutes = startTime.diff(DateTime.now(), "minutes").minutes;
  const rounded = Math.round(diffMinutes);
  if (Math.abs(rounded) === 0) {
    return { subtitle: "Starting now", right: "Starting now" };
  }

  if (diffMinutes < 0) {
    const minutesAgo = Math.abs(rounded);
    return {
      subtitle: `Started ${minutesAgo} min ago`,
      right: `Started ${minutesAgo} min ago`,
    };
  }

  return {
    subtitle: `Starting soon in ${rounded} min`,
    right: `Starting in ${rounded} min`,
  };
};

export const ItemListAsyncCalendarEventRowView: FC<ItemListAsyncCalendarEventRowViewProps> = ({
  itemRowData,
  startTime,
  endTime,
  isAllDay,
  isHighlighted,
  isHoverable,
  isDisabled,
  hasAssociatedNote,
  handleItemClick,
  handleMouseEnter,
}) => {
  const item = itemRowData.payload;

  const itemRef = useRef(item);
  itemRef.current = item;

  const startTimeDateTime = startTime ? DateTime.fromISO(startTime) : null;
  const now = DateTime.now();
  const diffInMinutes = startTimeDateTime ? startTimeDateTime.diff(now, "minutes").minutes : null;
  const isUpcomingCalendarEvent =
    diffInMinutes !== null && diffInMinutes <= UPCOMING_EVENT_THRESHOLD_MINUTES;
  const isImminentCalendarEvent =
    diffInMinutes !== null && diffInMinutes <= IMMINENT_EVENT_THRESHOLD_MINUTES;

  const handleRowClick = (event: ReactMouseEvent) =>
    handleItemClick(event, !itemRowData.mustClickOnRowTitle);
  const handleTitleClick = (event: ReactMouseEvent) => handleItemClick(event);

  const showActions = isHighlighted && !!item.dropdown;
  const appearance = isUpcomingCalendarEvent
    ? MDSRowItemAppearance.UpcomingCalendarEvent
    : MDSRowItemAppearance.Standard;

  const subtitleText: string = useMemo(() => {
    if (isImminentCalendarEvent) {
      return getImminentEventLabels({ startTime: startTimeDateTime! }).subtitle;
    } else {
      return formatCalendarEventTime(startTime, endTime, isAllDay);
    }
  }, [startTime, endTime, isAllDay, isImminentCalendarEvent, startTimeDateTime]);

  const rightLabel: ReactNode = useMemo(() => {
    if (isUpcomingCalendarEvent) {
      const { right } = getImminentEventLabels({ startTime: startTimeDateTime! });
      return (
        <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Emphasis}>
          {right}
        </MDSText>
      );
    }
    if (startTime) {
      return (
        <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
          {formatDateString(startTime, "ccc M/d")}
        </MDSText>
      );
    }

    return null;
  }, [startTime, isUpcomingCalendarEvent, startTimeDateTime]);

  return (
    <MDSRowItem
      $isSelected={false}
      $isHighlighted={isHighlighted}
      $isSpecial={item.isSpecial}
      $appearance={appearance}
      $mustClickOnRowTitle={itemRowData.mustClickOnRowTitle ?? false}
      $isDisabled={isDisabled}
      onClick={handleRowClick}
      onMouseEnter={isHoverable ? handleMouseEnter : undefined}
    >
      <StyledIconWrapper slot={MDSRowItemSlotName.LeftAddon}>
        <MDSIcon
          size={18}
          kind={hasAssociatedNote ? MDSIconKind.Document : MDSIconKind.CalendarEvent}
          color={isImminentCalendarEvent ? MDSIconColor.Emphasis : MDSIconColor.Placeholder}
        />
      </StyledIconWrapper>
      <ContentRow>
        <Content>
          <LabelContainer onClick={handleTitleClick}>
            <MDSText
              variant={MDSTextVariant.TextM}
              weight={MDSTextWeight.Smedium}
              color={isImminentCalendarEvent ? MDSTextColor.Emphasis : MDSTextColor.Primary}
            >
              {item.label}
            </MDSText>
          </LabelContainer>
        </Content>
      </ContentRow>

      <BottomRow slot={MDSRowItemSlotName.BottomAddon}>
        <MDSText
          variant={MDSTextVariant.TextS}
          color={isImminentCalendarEvent ? MDSTextColor.Emphasis : MDSTextColor.Primary}
          weight={isImminentCalendarEvent ? MDSTextWeight.Smedium : MDSTextWeight.Regular}
        >
          {subtitleText}
        </MDSText>
      </BottomRow>

      {!showActions && <RightLabelContainer>{rightLabel}</RightLabelContainer>}

      {showActions && (
        <RightSide slot={MDSRowItemSlotName.RightAddon}>
          {item.dropdown && (
            <>
              <MDSSpacer />
              <ItemListRowDropdown itemId={item.id} dropdown={item.dropdown} />
            </>
          )}
        </RightSide>
      )}
    </MDSRowItem>
  );
};

const ContentRow = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  flex-shrink: 1;
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
`;

const LabelContainer = styled.div`
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const RightLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing["1"]};

  min-width: 0;
  margin-left: 10px;
  flex-shrink: 0;

  @container mds-row-item (max-width: ${MDSPanelWidths.headerBreakpoint}px) {
    width: min-content;
  }
`;

const BottomRow = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.secondary};
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const RightSide = styled.div`
  align-items: flex-end;
  display: flex;
  margin-left: 10px;
  width: 60px;
`;

const StyledIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  min-width: fit-content;
  align-self: stretch;
  padding-right: ${({ theme }) => theme.spacing["3"]};
  padding-top: 2px;
`;
